import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import UIMock from "@/studio/components/UIMock";

gsap.registerPlugin(ScrollTrigger);

/**
 * (02) SERVICES — pinned editorial card stack that BUILDS UP.
 *
 * Direction (Feb 2026, services pass):
 *  • Section pins.
 *  • Initially only Card 1 visible at center.
 *  • Scroll → Card 2 rises from below and covers Card 1.
 *  • Card 1 scales down and stays visible, fanned upward behind Card 2.
 *  • Each next card covers the previous and pushes the rest further back.
 *  • Result: a heavy, editorial deck — cards feel physically attached.
 *
 *  No fading-out. No carousel. No black hole. No particles. No WebGL.
 */

const SERVICES = [
  "Websites",
  "Platforms",
  "AI Systems",
  "Internal Tools",
  "Automation",
  "SEO & Growth",
];

const CARDS = [
  {
    n: "01",
    title: "WEBSITES",
    mock: "website",
    desc: "Premium websites designed to shape perception.",
    examples: ["Company sites", "Landing pages", "Content hubs", "Immersive experiences"],
  },
  {
    n: "02",
    title: "PLATFORMS",
    mock: "dashboard",
    desc: "Products built around users.",
    examples: ["SaaS", "Dashboards", "Portals", "Internal systems"],
  },
  {
    n: "03",
    title: "AI SYSTEMS",
    mock: "ai",
    desc: "Real intelligence wired into your workflow.",
    examples: ["Assistants", "Copilots", "Voice AI", "Agents"],
  },
  {
    n: "04",
    title: "INTERNAL TOOLS",
    mock: "table",
    desc: "Tools your team actually wants to use.",
    examples: ["CRM", "ERP", "Admin", "Analytics", "Operations"],
  },
  {
    n: "05",
    title: "AUTOMATION",
    mock: "pipeline",
    desc: "Quiet systems that do the work, 24/7.",
    examples: ["Lead pipelines", "Integrations", "Outreach", "Workflows", "Monitoring"],
  },
  {
    n: "06",
    title: "SEO & GROWTH",
    mock: "analytics",
    desc: "A website without visitors is just an expensive brochure.",
    examples: ["Technical SEO", "Metadata", "Structured data", "Performance", "Analytics", "Conversion paths"],
  },
];

// Stack slots — depth 0 = active, depth N = oldest receded card (fanned up).
const STACK_SLOTS = [
  { y: 0,   x: 0,  scale: 1.0,  zIndex: 100 }, // active
  { y: -4,  x: -2, scale: 0.96, zIndex: 99 },
  { y: -8,  x:  3, scale: 0.92, zIndex: 98 },
  { y: -12, x: -2, scale: 0.88, zIndex: 97 },
  { y: -16, x:  2, scale: 0.84, zIndex: 96 },
  { y: -20, x: -1, scale: 0.80, zIndex: 95 },
];

function ServiceCard({ card, index, refSetter }) {
  return (
    <article
      ref={(el) => refSetter(index, el)}
      data-testid={`service-card-${card.n}`}
      className="stack-card relative mb-6 overflow-hidden rounded-[32px] border border-white/[0.08] bg-[#070708] shadow-[0_40px_120px_-40px_rgba(0,0,0,0.95)] lg:absolute lg:inset-x-0 lg:top-0 lg:mb-0 lg:h-[560px]"
      style={{ willChange: "transform, opacity" }}
    >
      <div className="grid h-full grid-cols-1 lg:grid-cols-[1.15fr_1fr]">
        {/* Visual artefact */}
        <div className="relative aspect-[16/10] border-b border-white/[0.06] bg-[#040405] lg:aspect-auto lg:border-b-0 lg:border-r">
          <div className="absolute inset-8 md:inset-10">
            <UIMock kind={card.mock} className="h-full" />
          </div>
        </div>

        {/* Editorial content */}
        <div className="flex flex-col justify-between gap-6 p-8 md:p-10 lg:p-12">
          <div className="flex items-start justify-between gap-6">
            <div className="min-w-0">
              <div className="ff-ui text-[11px] font-medium tracking-[0.28em] text-white/35">
                {card.n} — Service
              </div>
              <h3 className="ff-display mt-3 text-4xl font-extrabold leading-[1.02] tracking-tight text-white md:text-5xl">
                {card.title}
              </h3>
              <p className="mt-4 max-w-md text-[15px] leading-relaxed text-white/55">
                {card.desc}
              </p>
            </div>
            <span className="ff-display flex-shrink-0 text-7xl font-extrabold leading-none text-white/[0.05] md:text-8xl">
              {card.n}
            </span>
          </div>

          <ul className="space-y-3 border-t border-white/[0.06] pt-6">
            {card.examples.map((ex) => (
              <li
                key={ex}
                className="ff-display flex items-baseline gap-3 text-base font-semibold text-white/80 md:text-lg"
              >
                <span className="h-1 w-1 rounded-full bg-white/35" />
                {ex}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </article>
  );
}

export default function WhatWeBuild() {
  const navigate = useNavigate();
  const sectionRef = useRef(null);
  const cardRefs = useRef([]);
  const setCardRef = (i, el) => {
    cardRefs.current[i] = el;
  };

  useEffect(() => {
    const cards = cardRefs.current.filter(Boolean);
    if (!sectionRef.current || cards.length < 2) return undefined;

    const mm = gsap.matchMedia();

    mm.add("(min-width: 1024px)", () => {
      const N = cards.length;

      // Initial: card 0 active, others parked just below the viewport area.
      cards.forEach((el, i) => {
        if (i === 0) {
          gsap.set(el, STACK_SLOTS[0]);
        } else {
          gsap.set(el, {
            yPercent: 110,
            xPercent: 0,
            scale: 0.96,
            opacity: 1,
            zIndex: 100 - i,
          });
        }
      });

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "top top",
          end: () => `+=${(N - 1) * window.innerHeight}`,
          pin: true,
          scrub: 0.55,
          anticipatePin: 1,
          invalidateOnRefresh: true,
        },
      });

      for (let step = 0; step < N - 1; step++) {
        // Incoming card rises from below to active slot
        tl.to(
          cards[step + 1],
          {
            yPercent: STACK_SLOTS[0].y,
            xPercent: STACK_SLOTS[0].x,
            scale: STACK_SLOTS[0].scale,
            zIndex: STACK_SLOTS[0].zIndex,
            ease: "power3.out",
            duration: 1,
          },
          step,
        );

        // Already-shown cards fan upward into successively deeper slots
        for (let i = 0; i <= step; i++) {
          const depth = step + 1 - i; // 1 = just receded, larger = older
          const slot =
            STACK_SLOTS[Math.min(depth, STACK_SLOTS.length - 1)];
          tl.to(
            cards[i],
            {
              yPercent: slot.y,
              xPercent: slot.x,
              scale: slot.scale,
              zIndex: slot.zIndex,
              ease: "power3.out",
              duration: 1,
            },
            step,
          );
        }
      }
    });

    return () => mm.revert();
  }, []);

  return (
    <section
      ref={sectionRef}
      id="services"
      data-testid="services-section"
      className="relative bg-[var(--paper)] py-24 md:py-32"
    >
      <div className="mx-auto grid max-w-[1500px] grid-cols-12 gap-10 px-5 md:px-10 lg:gap-14">
        {/* LEFT — editorial intro + CTA */}
        <div className="col-span-12 lg:col-span-4">
          <span className="ff-ui label text-[var(--ink-soft)]">
            (02) — Services
          </span>
          <h2 className="ff-display mt-6 text-6xl font-extrabold leading-[0.92] tracking-tight md:text-7xl">
            SERVICES
          </h2>
          <p className="mt-6 max-w-sm text-[15px] leading-relaxed text-[var(--ink-soft)]">
            We don&apos;t build pages. We build products designed to be used,
            scaled and remembered.
          </p>

          <ul className="mt-9 space-y-2.5 border-t border-[var(--line)] pt-6">
            {SERVICES.map((s) => (
              <li
                key={s}
                className="ff-display flex items-baseline gap-3 text-lg font-semibold text-[var(--ink)] md:text-xl"
              >
                <span className="h-1 w-1 rounded-full bg-[var(--ink-faint)]" />
                {s}
              </li>
            ))}
          </ul>

          <p className="ff-serif mt-9 max-w-xs text-lg leading-snug text-[var(--ink-soft)]">
            Built to launch.
            <br />
            Built to scale.
            <br />
            Built to stay.
          </p>

          <button
            type="button"
            onClick={() => navigate("/services")}
            data-cursor="big"
            data-testid="services-cta-explore"
            className="group mt-9 inline-flex items-center gap-2.5 rounded-full border border-[var(--ink)]/20 px-5 py-3 text-[13px] font-semibold text-[var(--ink)] transition-colors hover:border-[var(--ink)] hover:bg-[var(--ink)] hover:text-[var(--ink-on)]"
          >
            <span>Explore capabilities</span>
            <span className="transition-transform duration-300 group-hover:translate-x-0.5">
              →
            </span>
          </button>
        </div>

        {/* RIGHT — pinned card stack */}
        <div className="col-span-12 lg:col-span-8">
          <div
            data-testid="services-stack"
            className="relative lg:h-[78vh] lg:min-h-[640px]"
          >
            {CARDS.map((c, i) => (
              <ServiceCard
                key={c.n}
                card={c}
                index={i}
                refSetter={setCardRef}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
